/* THE UPLOAD TRANSPORT THAT CAN SAY HOW MANY BYTES HAVE LEFT — PHASE 6.5
 * STEP 1, 2026-09-13.
 *
 * `fetch` reports nothing about an upload's progress; only
 * `XMLHttpRequest` does (`xhr.upload.onprogress`). This is a `fetchImpl`
 * in `migrateOne`'s shape — `(url, { method, headers, body, signal,
 * onUploadProgress })` → something with `status` and `json()` — so the
 * app passes it in and the gate keeps passing its fake `fetch`. There is
 * one uploader either way.
 *
 * ── ⚠ WHAT THE NUMBER IS, AND WHAT IT IS NOT ────────────────────────
 * `onUploadProgress(sent, total)` counts request bytes on the wire. When
 * `sent === total` the body has left the device and the container's
 * encode-and-store begins, which reports nothing until the response. The
 * measured ~10 s per upload (2026-08-28, a 3 MB HEIC) is mostly that
 * second half, so on wifi the number reaches 100% in about a second and
 * the screen then says "Processing…" with no number — because there is
 * none. A bar that crept to 100% over the ten seconds would be a caption
 * claiming a quantity nobody measured.
 *
 * ── ⚠⚠ ABORT ───────────────────────────────────────────────────────
 * The `signal` aborts the XHR. That stops this side waiting; it does not
 * unsend a body the server already has. Callers treat an abort as
 * `cancelled`, and `uploadSlot`'s generation check is what makes a
 * landing from an aborted request harmless.
 *
 * ⚠ BROWSER ONLY. There is no XHR in Node; no gate drives this file. Its
 * proof is the walk, and BUILD_NOTES says so.
 */

export function xhrUpload(url, { method = 'POST', headers = {}, body = null, signal = null, onUploadProgress = null } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method, url, true)
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v)
    xhr.responseType = 'text'

    let lastTotal = 0
    if (onUploadProgress && xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return
        lastTotal = e.total
        try { onUploadProgress(e.loaded, e.total) } catch (err) { /* a reporter must not stop an upload */ }
      }
      /* The last progress event is not guaranteed to say loaded === total;
       * `upload.onload` is the event that means the body has left. */
      xhr.upload.onload = () => {
        const t = lastTotal || (body && body.byteLength) || 1
        try { onUploadProgress(t, t) } catch (err) { /* likewise */ }
      }
    }

    xhr.onload = () => resolve({
      ok: xhr.status >= 200 && xhr.status < 300,
      status: xhr.status,
      headers: { get: (name) => xhr.getResponseHeader(name) },
      json: async () => JSON.parse(xhr.responseText),
      text: async () => xhr.responseText,
    })
    xhr.onerror = () => reject(new TypeError('network error during upload'))
    xhr.ontimeout = () => reject(new TypeError('upload timed out'))
    xhr.onabort = () => {
      const err = new Error('upload aborted')
      err.name = 'AbortError'
      reject(err)
    }

    if (signal) {
      if (signal.aborted) { xhr.onabort(); return }
      signal.addEventListener('abort', () => xhr.abort(), { once: true })
    }
    xhr.send(body)
  })
}
